import { parse } from "@formkit/tempo";
import { z } from "zod";

import { brandString, TBrand } from "@/utils/branded";
import { pad, safeParseInt } from "@/utils/number";

export enum EStringTimeFormat {
  DATETIME_FORMAT = "YYYY-MM-DDTHH:mm:ss",
  DATE_STRING_FORMAT = "YYYY-MM-DD",
  LOCAL_TIME = "HH:mm:ss",
}

export type TDateTime = TBrand<string, "DateTime">;
export type TLocalDate = TBrand<string, "LocalDate">;
export type TLocalTime = TBrand<string, "LocalTime">;

function stringTimeSchema(format: EStringTimeFormat) {
  return z.string().superRefine((str, ctx) => {
    try {
      parse(str, format);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (ignored) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Invalid date format (expected: ${format}).`,
      });
    }
  });
}

export const stringDateTimeSchema = stringTimeSchema(EStringTimeFormat.DATETIME_FORMAT).transform((str) =>
  brandString<TDateTime>(str),
);

export const stringLocalDateSchema = stringTimeSchema(EStringTimeFormat.DATE_STRING_FORMAT).transform((str) =>
  brandString<TLocalDate>(str),
);

export const stringLocalTimeSchema = stringTimeSchema(EStringTimeFormat.LOCAL_TIME).transform((str) =>
  brandString<TLocalTime>(str),
);

export enum ETimeUnit {
  SECOND = "SECOND",
  MINUTE = "MINUTE",
  HOUR = "HOUR",
  DAY = "DAY",
  WEEK = "WEEK",
  MONTH = "MONTH",
  QUARTER = "QUARTER",
  YEAR = "YEAR",
}

const secondsPerUnits: { [k in ETimeUnit]: number } = {
  [ETimeUnit.SECOND]: 1,
  [ETimeUnit.MINUTE]: 60,
  [ETimeUnit.HOUR]: 60 * 60,
  [ETimeUnit.DAY]: 60 * 60 * 24,
  [ETimeUnit.WEEK]: 60 * 60 * 24 * 7,
  [ETimeUnit.MONTH]: 60 * 60 * 24 * 30,
  [ETimeUnit.QUARTER]: 60 * 60 * 24 * 30 * 3,
  [ETimeUnit.YEAR]: 60 * 60 * 24 * 365,
};

export function getSecondsInUnit(unit?: ETimeUnit) {
  return unit ? secondsPerUnits[unit] : secondsPerUnits.HOUR;
}

export const MAX_DATE_TIME = new Date(new Date().setHours(23, 59, 0));
export const MIN_STRING_TIME = "00:00";
export const MAX_STRING_TIME = "23:59";

/**
 * Converts string time into number time
 * @param time string time in format HH:mm
 */
export function toTimeNumber(time: string): number {
  const hours = getHours(time);
  const minutes = getMinutes(time);

  return Math.round(((hours % 24) + (minutes % 60) / 60) * 100) / 100;
}

/**
 *
 * @param time in format HH:mm
 * @returns
 */
function toMinutes(time: string): number {
  return getHours(time) * 60 + getMinutes(time);
}

/**
 *
 * @param timeInMin
 * @returns time in format HH:mm
 */
function toStringTime(timeInMin: number): string {
  const hours = Math.floor(timeInMin / 60);
  const minutes = timeInMin % 60;
  return `${pad(hours.toString())}:${pad(minutes.toString())}`;
}

/**
 *
 * @param startTime HH:mm
 * @param endTime HH:mm
 * @returns HH:mm
 */
export function getMiddleTime(startTime: string, endTime: string): string {
  const startTimeInMinutes = toMinutes(startTime);
  const endTimeInMinutes = toMinutes(endTime);
  return toStringTime(toMinutes(startTime) + Math.round((endTimeInMinutes - startTimeInMinutes) / 2));
}

/**
 *
 * @param time HH:mm
 */
export function getHours(time: string): number {
  return safeParseInt(time.split(":")[0]);
}

/**
 *
 * @param time HH:mm
 */
export function getMinutes(time: string): number {
  return safeParseInt(time.split(":")[1]);
}

/**
 *
 * @param time HH:mm
 */
export function getSeconds(time: string): number {
  return safeParseInt(time.split(":")[2]);
}

/**
 *
 * @param time HH:mm
 */
export function addMinutes(time: string, minutes: number): string {
  return toStringTime(toMinutes(time) + minutes);
}

/**
 *
 * @param time HH:mm
 */
export function substractMinutes(time: string, minutes: number): string {
  return toStringTime(toMinutes(time) - minutes);
}

/**
 * Compares only hours and minutes of startTime and endTime
 * @param startTime
 * @param endTime
 */
export function dateMinutesDiff(startTime: Date, endTime: Date): number {
  const startTimeInMinutes = startTime.getHours() * 60 + startTime.getMinutes();
  const endTimeInMinutes = endTime.getHours() * 60 + endTime.getMinutes();

  return endTimeInMinutes - startTimeInMinutes;
}

export function minutesDiff(startTime: string, endTime: string): number {
  return toMinutes(endTime) - toMinutes(startTime);
}

// slider range goes from 0 to 100
export function getDayTimeSliderValue(date: Date, maxHour: number = 23, maxMin: number = 59): number {
  const decimalHoursDate = date.getHours() + date.getMinutes() / 60;
  const maxTime = maxHour + maxMin / 60;

  return (decimalHoursDate / maxTime) * 100;
}

function getDecimalHoursDateFromSliderValue(value: number, maxHour: number = 23, maxMin: number = 59): number {
  const maxTime = maxHour + maxMin / 60;
  return (value * maxTime) / 100;
}

export function getDayHourFromSliderValue(value: number, maxHour: number = 23, maxMin: number = 59): number {
  return Math.floor(getDecimalHoursDateFromSliderValue(value, maxHour, maxMin));
}

export function getDayMinutesFromSliderValue(value: number, maxHour: number = 23, maxMin: number = 59): number {
  return Math.floor(
    (getDecimalHoursDateFromSliderValue(value, maxHour, maxMin) - getDayHourFromSliderValue(value, maxHour, maxMin)) *
      60,
  );
}
